import { Phase } from "@design-system";

export interface PhaseDistributionPercentage {
  phaseId: number;
  percentage: number;
}

export interface TaskDistributionPercentage {
  taskId: number;
  percentage: number;
}

/**
 * Calculates even distribution percentages for phases
 */
export function calculateEvenDistributionPercentages(
  phases: Phase[]
): PhaseDistributionPercentage[] {
  if (phases.length === 0) return [];

  const evenPhasePercentage = parseFloat((100 / phases.length).toFixed(2));

  return phases.map((phase, index) => ({
    phaseId: phase.id,
    percentage:
      index === phases.length - 1
        ? parseFloat((100 - evenPhasePercentage * (phases.length - 1)).toFixed(2))
        : evenPhasePercentage,
  }));
}

/**
 * Calculates even distribution percentages for tasks within a phase
 */
export function calculateEvenTaskDistributionPercentages(
  phase: Phase
): TaskDistributionPercentage[] {
  if (!phase.tasks || phase.tasks.length === 0) return [];

  const taskCount = phase.tasks.length;
  const evenTaskPercentage = parseFloat((100 / taskCount).toFixed(2));

  return phase.tasks.map((task, index) => ({
    taskId: task.id,
    percentage:
      index === taskCount - 1
        ? parseFloat((100 - evenTaskPercentage * (taskCount - 1)).toFixed(2))
        : evenTaskPercentage,
  }));
}

/**
 * Calculates time-based percentages for phases based on work estimates
 */
export function calculateProportionateDistributionPercentages(
  phases: Phase[]
): PhaseDistributionPercentage[] {
  if (phases.length === 0) return [];

  const phaseTimeEstimates = phases.map((phase) => {
    let phaseTimeTotal = 0;

    if (phase.work && phase.work.length > 0) {
      phaseTimeTotal = phase.work.reduce((total, work) => {
        if (work.timeSingle && work.timeSingle > 0) {
          return total + work.timeSingle;
        }

        if (work.timeLower && work.timeUpper) {
          return total + (work.timeLower + work.timeUpper) / 2;
        }

        if (work.timeLower && work.timeLower > 0) {
          return total + work.timeLower;
        }

        if (work.timeUpper && work.timeUpper > 0) {
          return total + work.timeUpper;
        }

        return total;
      }, 0);
    }

    return {
      phaseId: phase.id,
      timeEstimate: phaseTimeTotal > 0 ? phaseTimeTotal : 1,
    };
  });

  const totalTimeEstimate = phaseTimeEstimates.reduce((sum, phase) => sum + phase.timeEstimate, 0);

  const phaseProportions = phaseTimeEstimates.map((phase, index) => {
    const rawPercentage = (phase.timeEstimate / totalTimeEstimate) * 100;
    return {
      phaseId: phase.phaseId,
      percentage: parseFloat(rawPercentage.toFixed(2)),
    };
  });

  // Adjust the last phase to ensure the total is exactly 100%
  const calculatedTotal = phaseProportions.reduce((sum, p) => sum + p.percentage, 0);
  if (calculatedTotal !== 100 && phaseProportions.length > 0) {
    const lastPhase = phaseProportions[phaseProportions.length - 1];
    lastPhase.percentage = parseFloat((lastPhase.percentage + (100 - calculatedTotal)).toFixed(2));
  }

  return phaseProportions;
}

/**
 * Checks if any phase has time estimates
 */
export function hasTimeEstimates(phases: Phase[]): boolean {
  return phases.some((phase) =>
    phase.work?.some(
      (work) =>
        (work.timeSingle && work.timeSingle > 0) ||
        (work.timeLower && work.timeLower > 0) ||
        (work.timeUpper && work.timeUpper > 0)
    )
  );
}
